import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  tableCellClasses,
  tableClasses,
  tableRowClasses,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Tier } from '../../../../../shared/types/tiers';
import { getCurrencySignForAreaUnit } from '../../../../../shared/helpers/functions/currency';
import { AreaUnit } from '../../../../../shared/graphql/graphql';
import { getShortAreaUnit } from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import { formatNumber } from '../../../../../shared/helpers/functions/number';
import { DASH_SYMBOL } from '../../../../../shared/helpers/constants/markup';

export default function SubscriptionPreviewTable({
  areaUnit,
  tiers,
  seats,
  planet,
}: {
  areaUnit: AreaUnit;
  tiers: Tier[];
  seats: {
    amount: number;
    price: number;
    cost: number;
  };
  planet: {
    amount: number;
    price: number;
    cost: number;
  };
}) {
  const { t } = useTranslation();
  const currencySign = getCurrencySignForAreaUnit(areaUnit);
  const shortAreaUnit = getShortAreaUnit(areaUnit);
  const maxLimitExceeded = tiers.some((tier) => Number.isNaN(tier.totalPrice));

  return (
    <TableContainer>
      <Table
        sx={(theme) => ({
          [`& .${tableCellClasses.root}`]: {
            color: theme.palette.secondary.main,
            fontSize: '12px',
            lineHeight: '16px',
            p: '8px 4px',
            '&:first-of-type': { pl: 0 },
            '&:last-of-type': { pr: 0 },
          },
        })}
      >
        <TableBody>
          <TableRow>
            <TableCell colSpan={maxLimitExceeded ? 3 : 4} sx={{ border: '0' }}>
              {t('plans.annual.title')}:
            </TableCell>
            {maxLimitExceeded && (
              <TableCell sx={{ border: '0', textAlign: 'right' }}>
                {DASH_SYMBOL}
              </TableCell>
            )}
          </TableRow>
          {!maxLimitExceeded &&
            tiers.map((tier, index) => (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    '&:before': { content: '"• "' },
                    [`.${tableClasses.root} .${tableRowClasses.root} &`]: {
                      '&:first-of-type': { pl: 1 },
                      p: 0.5,
                    },
                    ...(index !== tiers.length - 1 && { borderBottom: 0 }),
                  }}
                >
                  {index === 0
                    ? t('subscription-table.annual-subscription-first-line', {
                        amount: formatNumber(tier.max),
                        areaUnit: shortAreaUnit,
                      })
                    : t('subscription-table.annual-subscription-next-line', {
                        from: formatNumber(tier.min),
                        to: formatNumber(tier.max),
                        areaUnit: shortAreaUnit,
                      })}
                </TableCell>
                <TableCell
                  sx={{
                    ...(index !== tiers.length - 1 && { borderBottom: 0 }),
                  }}
                >
                  {formatNumber(tier.amount)} {shortAreaUnit}
                </TableCell>
                <TableCell
                  sx={{
                    ...(index !== tiers.length - 1 && { borderBottom: 0 }),
                  }}
                >
                  {currencySign}
                  {formatNumber(tier.unitPrice.toFixed(3))}
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'right',
                    ...(index !== tiers.length - 1 && { borderBottom: 0 }),
                  }}
                >
                  {currencySign}
                  {formatNumber(tier.totalPrice.toFixed(3))}
                </TableCell>
              </TableRow>
            ))}
          <TableRow>
            <TableCell>{t('subscription-table.user-seat')}</TableCell>
            <TableCell>{seats.amount}</TableCell>
            <TableCell>
              {currencySign}
              {formatNumber(seats.price.toFixed(3))}
            </TableCell>
            <TableCell sx={{ textAlign: 'right' }}>
              {currencySign}
              {formatNumber(seats.cost.toFixed(3))}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t('subscription-table.planet-imagery')}</TableCell>
            <TableCell>
              {formatNumber(planet.amount)} {shortAreaUnit}
            </TableCell>
            <TableCell>
              {currencySign}
              {formatNumber(planet.price.toFixed(3))}
            </TableCell>
            <TableCell
              sx={{
                textAlign: 'right',
              }}
            >
              {currencySign}
              {formatNumber(planet.cost.toFixed(3))}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
